// Globals
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import {
  StyledCard,
  StyledCardRow,
  StyledCardValue,
  StyledCardTitle,
  StyledHighlightedRow,
  StyledTitleRow,
  StyledCardComment,
  StyledTooltipContent,
  StyledInfoWrapper,
  StyledPeopleCount,
  StyledBidAlertBadge,
} from './AttendeeCardStyles';
import { CommentIcon, PhoneAlignedIcon, EmailIcon, ClockAlignedIcon } from '@/icons';

// Models
import { IViewingAttendee } from '@/models/viewing.model';

// Helpers
import { renderDate } from '@/utils';
import { DateFormats } from '@/constants/dates';
import { Tooltip } from '@/ui';
import { pickThemeColor } from '@/theme';

interface IViewingAttendeeCardProps {
  attendee: IViewingAttendee;
}

const ViewingAttendeeCard: FC<IViewingAttendeeCardProps> = ({ attendee }) => {
  const { t } = useTranslation('viewing');

  return (
    <StyledCard>
      <StyledInfoWrapper>
        <StyledTitleRow>
          <StyledCardTitle>{`${attendee.first_name} ${attendee.last_name}`}</StyledCardTitle>
          {attendee.is_bid_alert_only ? (
            <StyledBidAlertBadge>{t('viewing_bid_alert_only_label')}</StyledBidAlertBadge>
          ) : (
            <StyledPeopleCount>
              {t(attendee.participants_count > 1 ? 'viewing_people_count' : 'viewing_person_count', {
                count: attendee.participants_count,
              })}
            </StyledPeopleCount>
          )}
        </StyledTitleRow>
        <StyledCardRow>
          <EmailIcon />
          <StyledCardValue>{attendee.email}</StyledCardValue>
        </StyledCardRow>
        {attendee.viewing_time && (
          <StyledCardRow>
            <ClockAlignedIcon />
            <StyledCardValue>{renderDate(attendee.viewing_time, DateFormats.DAY_MONTH_WITH_TIME)}</StyledCardValue>
          </StyledCardRow>
        )}
        <StyledCardRow>
          <PhoneAlignedIcon />
          <StyledCardValue>{attendee.phone_number}</StyledCardValue>
        </StyledCardRow>
      </StyledInfoWrapper>

      {attendee.comment && (
        <Tooltip
          title={<StyledTooltipContent>{attendee.comment}</StyledTooltipContent>}
          color={pickThemeColor('DARK_PURPLE')}
          overlayClassName="custom-tooltip"
        >
          <StyledHighlightedRow>
            <CommentIcon />
            <StyledCardComment>{attendee.comment}</StyledCardComment>
          </StyledHighlightedRow>
        </Tooltip>
      )}
    </StyledCard>
  );
};

export default ViewingAttendeeCard;
